import { Formik } from 'formik'
import { pipe, reject, append, when, reduce } from 'ramda'
import React, { useContext, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useFormikContext } from 'formik'
import styled from 'styled-components'
import CloseButton from '@/components/close-button'
import { createModal, useModalize } from '@/components/modalize'
import { select } from '@/store'
import { breakpoints, fontWeights, colors } from '@/styles'
import { Sortable } from '@/components/sortable'
import ExpandableTextArea from './expandable-text-area'

const Context = React.createContext({})

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  flex-shrink: 0;
  max-height: 667px;
  max-width: 767px;
  position: relative;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    max-height: 100vh;
    height: calc(100% - 68px);
    border-radius: 16px 16px 0 0;
  }
`

const FormWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
`

const FormContentWrapper = styled(FormWrapper)`
  padding: 0 30px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 0 20px;
  }
`

const Title = styled.h3`
  font-size: 16px;
  font-weight: ${fontWeights.bold};
  line-height: 35px;
  margin-bottom: 8px;
  padding: 0px;
  text-align: left;
`

const FieldWrapper = styled.div`
  z-index: 550;
  margin-top: 16px;
  width: 100%;
`

const ScrollWrapper = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
  padding: 0px;
  padding-bottom: 60px;
`

const TopContainer = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  flex-direction: column;
  padding: 24px;
`

const DescriptionText = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: ${colors.grey48};
  text-align: left;
`

const idNewMessage = 'new'
const maxTemplates = 10

const formatValues = pipe(
  reject(msg => !msg),
  when(msgs => msgs.length < maxTemplates, append({ id: idNewMessage, content: '' })),
  reduce(
    (total, currentItem) => ({
      ...total,
      [currentItem.id]: currentItem.content
    }),
    {}
  )
)

//insert message with generated id
const insertMessage = (messages, result, setValues) => {
  setValues(values => ({ ...values, [result.id]: result.content, [idNewMessage]: '' }))
  return append(
    {
      ...result
    },
    messages
  )
}

const removeMessage = (messages, targetId) => reject(message => message.id === targetId, messages)

const updateMessage = (messages, message) =>
  messages.map(msg => (msg.id === message.id ? message : msg))

const SortItem = ({ name, children, ...rest }) => {
  const { currentFormValues, setCurrentFormValues, expandedField, setExpandedField } =
    useContext(Context)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { values, setValues } = useFormikContext()
  const changeInputFocus = target => {
    setExpandedField(target)
  }
  const onMessageDelete = messageId => {
    setExpandedField(null)
    deleteMessage(messageId)
    setCurrentFormValues(removeMessage(currentFormValues, messageId))
  }
  const deleteMessage = async messageId => {
    if (messageId === idNewMessage) {
      setValues(values => ({ ...values, [idNewMessage]: '' }))
      return
    }
    await dispatch.chatPreset.deletePreset(messageId)
  }
  const onDone = messageId => {
    const send = async () => {
      setExpandedField(null)
      const message = {
        id: messageId,
        content: values[messageId]
      }
      const result = await saveMessage(message)
      setCurrentFormValues(
        messageId === idNewMessage
          ? insertMessage(currentFormValues, result, setValues)
          : updateMessage(currentFormValues, message)
      )
    }
    send()
  }
  const saveMessage = async message => {
    const { id, content } = message
    let result
    if (id === idNewMessage) {
      result = await dispatch.chatPreset.createPreset(content)
    } else {
      dispatch.chatPreset.modifyPreset(message)
    }
    return result
  }
  return (
    <FieldWrapper>
      <ExpandableTextArea
        isExpanded={expandedField === name}
        onClick={changeInputFocus}
        onDelete={onMessageDelete}
        onDone={onDone}
        name={name}
        maxLength={500}
        expandedHeight={100}
        previewPlaceholder="Add new template"
        editPlaceholder={t(
          'l.chat.reply_template_modal.edit.placeholder.label',
          'Please enter a message.'
        )}
        {...rest}
      />
    </FieldWrapper>
  )
}

const EditTemplateModal = () => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const presetMessages = useSelector(select.chatPreset.presets)
  const dispatch = useDispatch()
  const [currentFormValues, setCurrentFormValues] = useState(presetMessages)
  const [expandedField, setExpandedField] = useState()
  const items = useMemo(
    () =>
      currentFormValues.map((message, index) => ({
        id: message.id,
        useListeners: false,
        children: props => <SortItem key={message.id} name={message.id} {...props} />
      })),
    [currentFormValues]
  )
  const formOptions = useCallback(
    () => ({
      initialValues: formatValues(presetMessages)
    }),
    [presetMessages]
  )
  const updateMessagePosition = async (presetId, position) => {
    await dispatch.chatPreset.setPresetPosition({
      presetId,
      position
    })
  }
  const onReorder = (item, newIndex) => {
    updateMessagePosition(item.id, newIndex + 1)
  }
  return (
    <Context.Provider
      value={{ currentFormValues, setCurrentFormValues, expandedField, setExpandedField }}
    >
      <Wrapper>
        <TopContainer>
          <Title>{t('l.chat.reply_template_modal.title.text', 'Quick Reply Templates')}</Title>
          <DescriptionText>
            {t(
              'b.chat.reply_templates.description.text',
              'Save time replying to inquiries with our customized quick reply templates.'
            )}
          </DescriptionText>
          <CloseButton top={29} right={29} onClick={close} />
        </TopContainer>
        <ScrollWrapper>
          <Formik {...formOptions()}>
            {() => (
              <FormWrapper>
                <FormContentWrapper>
                  <Sortable
                    items={items}
                    setItems={setCurrentFormValues}
                    onReorder={onReorder}
                    lock="y"
                  />
                  {currentFormValues.length < maxTemplates && (
                    <SortItem
                      isReorderEnabled={false}
                      name={idNewMessage}
                      previewPlaceholder="Add new template"
                      editPlaceholder={t(
                        'l.chat.reply_template_modal.edit.placeholder.label',
                        'Please enter a message.'
                      )}
                    />
                  )}
                </FormContentWrapper>
              </FormWrapper>
            )}
          </Formik>
        </ScrollWrapper>
      </Wrapper>
    </Context.Provider>
  )
}

export const show = createModal(EditTemplateModal, {
  width: 'auto',
  maxWidth: '482px',
  flexGrow: true,
  border: '1px solid #d8d8d8',
  mobileAlign: 'flex-end',
  tabletFullWidth: true
})
